import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Search, Scale, Filter, X, FileText, User, Hash, MapPin, Building2, Archive, ArrowRight } from "lucide-react";
import AppHeader from "../components/AppHeader";
import { api, formatApiError } from "../lib/api";

// Fase 1 — CRUD de expedientes y subida de documentos (backbone).
// Fase 2 añadirá: extracción OCR + campos + resolución de contradicciones.
// Fase 3 añadirá: comparables + mapa + informe HTML/PDF + auditoría.

const STATUS_COLORS = {
  borrador: "bg-stone-700 text-stone-300",
  extraccion_pendiente: "bg-amber-800 text-amber-200",
  revision_pendiente: "bg-orange-800 text-orange-100",
  comparables_pendientes: "bg-blue-800 text-blue-100",
  valorado: "bg-emerald-800 text-emerald-100",
  informe_generado: "bg-[#E0651F] text-white",
  archivado: "bg-stone-800 text-stone-500 line-through",
};

export default function AdminValoraciones() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("status", statusFilter);
      if (clientFilter) params.set("client_id", clientFilter);
      const { data } = await api.get(`/admin/valoraciones/cases?${params.toString()}`);
      setCases(data.cases || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error cargando expedientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/valoraciones/meta");
        setMeta(data);
      } catch (e) {
        // meta is best-effort
      }
    })();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, clientFilter]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="admin-valoraciones-page">
      <AppHeader />
      <header className="px-6 py-5 bg-stone-900 border-b border-stone-800">
        <div className="max-w-[1400px] mx-auto flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F]">FM Subastas &amp; Investment</div>
            <h1 className="font-display text-3xl font-black tracking-tight text-white flex items-center gap-3">
              <Scale className="w-7 h-7 text-[#E0651F]" /> Valoraciones
            </h1>
            <div className="text-sm text-stone-400 mt-1">
              Expedientes de valoración de inmuebles en subasta — {total} en total.
            </div>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
            data-testid="btn-new-case"
          >
            <Plus className="w-4 h-4" /> Nuevo expediente
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-4">
        {/* Filters */}
        <form onSubmit={onSearch} className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[250px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por ID subasta, dirección, ref. catastral o cliente…"
              className="w-full pl-10 pr-3 py-2 bg-stone-800 border border-stone-700 rounded-sm text-sm text-stone-100"
              data-testid="input-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 text-stone-100"
            data-testid="filter-status"
          >
            <option value="">Todos los estados</option>
            {meta?.statuses?.map((s) => (
              <option key={s} value={s}>{meta.status_labels[s] || s}</option>
            ))}
          </select>
          <button
            type="submit"
            className="border border-stone-700 hover:bg-stone-800 px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm text-stone-100"
          >
            Buscar
          </button>
        </form>

        {err && (
          <div className="bg-red-950/40 border border-red-900 text-red-300 p-3 rounded-sm text-sm" data-testid="err-msg">{err}</div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-stone-500 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando expedientes…
          </div>
        ) : cases.length === 0 ? (
          <div className="text-center py-20 bg-stone-900 border border-stone-800 rounded-sm">
            <Scale className="w-10 h-10 text-stone-700 mx-auto mb-3" />
            <div className="text-stone-400 mb-3">No hay expedientes todavía.</div>
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
              data-testid="btn-first-case"
            >
              <Plus className="w-4 h-4" /> Crear el primer expediente
            </button>
          </div>
        ) : (
          <div className="bg-stone-900 border border-stone-800 rounded-sm overflow-x-auto" data-testid="cases-list">
            <table className="w-full text-sm">
              <thead className="bg-stone-800/50 text-stone-400 text-[10px] uppercase tracking-[0.15em]">
                <tr>
                  <th className="text-left px-4 py-2">Cliente</th>
                  <th className="text-left px-4 py-2">ID subasta</th>
                  <th className="text-left px-4 py-2">Ref. catastral</th>
                  <th className="text-left px-4 py-2">Dirección</th>
                  <th className="text-left px-4 py-2">Tipo</th>
                  <th className="text-left px-4 py-2">Docs</th>
                  <th className="text-left px-4 py-2">Estado</th>
                  <th className="text-left px-4 py-2">Actualizado</th>
                  <th className="px-2"></th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/admin/valoraciones/${c.id}`)}
                    className="border-t border-stone-800/70 hover:bg-stone-800/40 cursor-pointer"
                    data-testid={`case-row-${c.id}`}
                  >
                    <td className="px-4 py-2 font-medium text-white">{c.client_name_cache || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-stone-300">{c.auction_id || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-stone-400">{c.cadastral_reference || "—"}</td>
                    <td className="px-4 py-2 text-stone-300 max-w-[260px] truncate">{c.address || "—"}</td>
                    <td className="px-4 py-2 text-stone-400 capitalize">{c.asset_type || "—"}</td>
                    <td className="px-4 py-2 text-stone-400">{c.document_count || 0}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-sm ${STATUS_COLORS[c.status] || "bg-stone-700"}`}>
                        {meta?.status_labels?.[c.status] || c.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-stone-500 text-[10px] font-mono">
                      {c.updated_at ? new Date(c.updated_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-2"><ArrowRight className="w-4 h-4 text-stone-600" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showNew && (
        <NewCaseModal
          meta={meta}
          onClose={() => setShowNew(false)}
          onCreated={(created) => {
            setShowNew(false);
            navigate(`/admin/valoraciones/${created.id}`);
          }}
        />
      )}
    </div>
  );
}


function NewCaseModal({ meta, onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientQ, setClientQ] = useState("");
  const [form, setForm] = useState({
    client_id: "",
    auction_id: "",
    address: "",
    cadastral_reference: "",
    asset_type: "vivienda",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/crm/clientes?limit=500");
        setClients(data.clientes || []);
      } catch (e) {
        setErr(formatApiError(e?.response?.data?.detail) || "Error cargando clientes");
      } finally {
        setLoadingClients(false);
      }
    })();
  }, []);

  const filteredClients = useMemo(() => {
    const q = clientQ.trim().toLowerCase();
    if (!q) return clients.slice(0, 30);
    return clients.filter((c) =>
      (c.nombre || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.telefono || "").toLowerCase().includes(q)
    ).slice(0, 30);
  }, [clients, clientQ]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { setErr("Selecciona un cliente"); return; }
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/admin/valoraciones/cases", form);
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error creando expediente");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="new-case-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-stone-900 border border-stone-800 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-800">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F]">Valoraciones</div>
            <h2 className="font-display text-xl font-black text-white">Nuevo expediente</h2>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-300" data-testid="close-new-case"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {err && <div className="bg-red-950/40 border border-red-900 text-red-300 p-2 rounded-sm text-sm">{err}</div>}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Cliente *</label>
            <input
              value={clientQ}
              onChange={(e) => setClientQ(e.target.value)}
              placeholder="Buscar por nombre, email o teléfono…"
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 text-stone-100 mb-2"
              data-testid="input-client-search"
            />
            {loadingClients ? (
              <div className="text-xs text-stone-500 py-2">Cargando clientes…</div>
            ) : filteredClients.length === 0 ? (
              <div className="text-xs text-stone-500 py-2 italic">
                Ningún cliente coincide. Crea uno en /admin/crm/clientes y vuelve.
              </div>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-stone-800 rounded-sm">
                {filteredClients.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-stone-800 last:border-b-0 hover:bg-stone-800/70 ${form.client_id === c.id ? "bg-[#E0651F]/20" : ""}`}
                    data-testid={`client-option-${c.id}`}
                  >
                    <input type="radio" checked={form.client_id === c.id} onChange={() => setForm({ ...form, client_id: c.id })} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{c.nombre || "(sin nombre)"}</div>
                      <div className="text-xs text-stone-500 truncate">{c.email} · {c.telefono || "sin tel"} {c.nif ? `· NIF ${c.nif}` : ""}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">ID subasta</label>
              <input
                value={form.auction_id}
                onChange={(e) => setForm({ ...form, auction_id: e.target.value })}
                placeholder="Ej: SUB-JA-2025-243100"
                className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 font-mono text-stone-100"
                data-testid="input-auction-id"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Ref. catastral</label>
              <input
                value={form.cadastral_reference}
                onChange={(e) => setForm({ ...form, cadastral_reference: e.target.value.toUpperCase() })}
                placeholder="Ej: 3503209VK2930S0001SD"
                maxLength={20}
                className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 font-mono uppercase text-stone-100"
                data-testid="input-cadastral"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Dirección</label>
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Calle, número, localidad, provincia"
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 text-stone-100"
              data-testid="input-address"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Tipo de activo</label>
            <select
              value={form.asset_type}
              onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 text-stone-100 capitalize"
              data-testid="input-asset-type"
            >
              {meta?.asset_types?.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Notas iniciales</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              maxLength={2000}
              className="w-full bg-stone-800 border border-stone-700 rounded-sm text-sm px-3 py-2 text-stone-100"
              placeholder="Contexto libre…"
              data-testid="input-notes"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-stone-700 hover:bg-stone-800 px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm text-stone-100">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !form.client_id}
              className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
              data-testid="btn-submit-case"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Crear expediente
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
